/**
 * TileManager — hybrid raster tile source switcher (BEACON-34 Phase 1)
 *
 * Source preference (highest → lowest):
 *   1. google     — Google Maps Tiles API (best satellite imagery, online only)
 *   2. mapbox     — Mapbox satellite-streets-v12 (cacheable, offline-capable)
 *   3. offline    — last cached tiles served from beacon-tiles-v1 cache
 *
 * Phase 1A: mapbox + offline detection wired. Status pill works.
 * Phase 1B (this commit): google session-token negotiation + preferred-source swap.
 *
 * Architecture decision: mapbox-gl-js is the renderer regardless of tile source.
 * We swap the *raster source* underneath the renderer rather than swapping
 * map libraries. This keeps the camera/UI/markers layer stable across switches.
 */

import { GoogleTileSession } from "./GoogleTileSession.js";

export const SOURCE_GOOGLE = "google";
export const SOURCE_MAPBOX = "mapbox";
export const SOURCE_OFFLINE = "offline";

const PREFERRED_ORDER = [SOURCE_GOOGLE, SOURCE_MAPBOX, SOURCE_OFFLINE];

export class TileManager extends EventTarget {
  /**
   * @param {Object} opts
   * @param {string} opts.googleApiKey - Google Maps Tiles API key (Phase 1B uses this)
   * @param {string} opts.mapboxToken  - Mapbox access token
   * @param {HTMLElement} [opts.statusEl] - Optional element for visible source indicator
   */
  constructor({ googleApiKey, mapboxToken, statusEl, forceSource } = {}) {
    super();
    this.googleApiKey = googleApiKey || null;
    this.mapboxToken = mapboxToken || null;
    this.statusEl = statusEl || null;

    /** @type {string|null} dev-only override; bypasses negotiation if set */
    this.forceSource = forceSource || null;
    if (this.forceSource && !PREFERRED_ORDER.includes(this.forceSource)) {
      console.warn(`[TileManager] unknown forceSource '${this.forceSource}' — ignoring`);
      this.forceSource = null;
    }

    /** @type {string} current resolved source */
    this.activeSource = SOURCE_OFFLINE;

    /** @type {Object} per-source health flags */
    this.health = {
      [SOURCE_GOOGLE]: { configured: !!googleApiKey, healthy: false, lastError: null },
      [SOURCE_MAPBOX]: { configured: !!mapboxToken, healthy: false, lastError: null },
      [SOURCE_OFFLINE]: { configured: true, healthy: true, lastError: null },
    };

    /** @type {GoogleTileSession|null} lazily initialized in negotiate() */
    this.googleSession = null;

    this._onlineHandler = () => this._handleOnline();
    this._offlineHandler = () => this._reconcile("offline-event");
    window.addEventListener("online", this._onlineHandler);
    window.addEventListener("offline", this._offlineHandler);
  }

  /** Tear down listeners (call from destroy/HMR teardown) */
  dispose() {
    window.removeEventListener("online", this._onlineHandler);
    window.removeEventListener("offline", this._offlineHandler);
  }

  /**
   * On online recovery, attempt to re-probe Google (in case we went offline
   * while Google was the active source and want to swap back in).
   */
  async _handleOnline() {
    if (this.health[SOURCE_GOOGLE].configured && !this.health[SOURCE_GOOGLE].healthy) {
      try {
        if (!this.googleSession) {
          this.googleSession = new GoogleTileSession({ apiKey: this.googleApiKey });
        }
        await this.googleSession.ensure();
        this.health[SOURCE_GOOGLE].healthy = true;
      } catch (err) {
        console.warn("[TileManager] Google re-probe on online failed:", err);
      }
    }
    if (this.health[SOURCE_MAPBOX].configured) {
      this.health[SOURCE_MAPBOX].healthy = true;
    }
    this._reconcile("online-event");
  }

  /**
   * Initial source negotiation. Call once after construction.
   * @returns {Promise<string>} resolved source id
   */
  async negotiate() {
    // Mapbox is always candidate-healthy if configured + online (no async probe step)
    if (this.health[SOURCE_MAPBOX].configured && navigator.onLine) {
      this.health[SOURCE_MAPBOX].healthy = true;
    }

    // Probe Google: try to get/refresh a session token. If it works, prefer it.
    if (this.health[SOURCE_GOOGLE].configured && navigator.onLine) {
      try {
        this.googleSession = new GoogleTileSession({ apiKey: this.googleApiKey });
        await this.googleSession.ensure();
        this.health[SOURCE_GOOGLE].healthy = true;
      } catch (err) {
        console.warn("[TileManager] Google probe failed:", err);
        this.health[SOURCE_GOOGLE].healthy = false;
        this.health[SOURCE_GOOGLE].lastError = String(err);
      }
    }

    this._reconcile("negotiate");
    return this.activeSource;
  }

  /**
   * Re-evaluate the active source based on current health + online state.
   * Called when connectivity changes or a source reports an error.
   */
  _reconcile(reason) {
    // Dev override: pin to forceSource if it's available.
    if (this.forceSource) {
      const h = this.health[this.forceSource];
      if (h?.configured && h?.healthy) {
        if (this.forceSource !== this.activeSource) {
          this._setSource(this.forceSource, `${reason}/force`);
        }
        return;
      }
      console.warn(
        `[TileManager] forceSource='${this.forceSource}' not healthy — falling through`
      );
    }

    const candidates = PREFERRED_ORDER.filter((s) => {
      const h = this.health[s];
      if (!h.configured || !h.healthy) return false;
      if (s !== SOURCE_OFFLINE && !navigator.onLine) return false;
      return true;
    });

    const chosen = candidates[0] || SOURCE_OFFLINE;
    if (chosen !== this.activeSource) {
      this._setSource(chosen, reason);
    }
  }

  _setSource(source, reason) {
    const previous = this.activeSource;
    this.activeSource = source;
    this._renderStatus();
    this.dispatchEvent(
      new CustomEvent("sourcechange", {
        detail: { source, previous, reason },
      })
    );
    console.info(`[TileManager] source: ${previous} → ${source} (${reason})`);
  }

  _renderStatus() {
    if (!this.statusEl) return;
    const labels = {
      [SOURCE_GOOGLE]: "Google",
      [SOURCE_MAPBOX]: "Mapbox",
      [SOURCE_OFFLINE]: "Offline (cached)",
    };
    this.statusEl.textContent = labels[this.activeSource] || this.activeSource;
    this.statusEl.dataset.source = this.activeSource;
  }

  /**
   * Build a mapbox-gl style spec for the active source.
   * @returns {Object} a partial mapboxgl.Style
   */
  buildStyle() {
    if (this.activeSource === SOURCE_MAPBOX) {
      // Use Mapbox's hosted satellite-streets style — it bundles good labels + roads on top
      // of high-res satellite imagery. URL form requires the Mapbox access token.
      return `mapbox://styles/mapbox/satellite-streets-v12`;
    }

    if (this.activeSource === SOURCE_GOOGLE && this.googleSession?.isValid()) {
      return this._googleStyle();
    }

    // Offline / no source — empty style, blank canvas. Service worker may serve cached
    // tiles from prior sessions when this exact tile URL pattern is requested.
    return {
      version: 8,
      sources: {},
      layers: [
        {
          id: "background",
          type: "background",
          paint: { "background-color": "#0a1f17" },
        },
      ],
    };
  }

  _googleStyle() {
    const tileUrl = this.googleSession.tileUrlTemplate();
    const tileSize = this.googleSession.tileSize || 256;
    return {
      version: 8,
      sources: {
        google: {
          type: "raster",
          tiles: [tileUrl],
          tileSize,
          attribution: "\u00a9 Google",
        },
      },
      layers: [
        {
          id: "google-base",
          type: "raster",
          source: "google",
          paint: { "raster-opacity": 1 },
        },
      ],
    };
  }

  /**
   * Mark a source as unhealthy (call from MapApp when tiles fail to load).
   * For Google specifically, also drops the cached session token — a stale
   * token (e.g., key was rotated) is the most likely cause of repeated errors.
   */
  reportError(source, error) {
    if (!this.health[source]) return;
    this.health[source].healthy = false;
    this.health[source].lastError = String(error);
    console.warn(`[TileManager] ${source} marked unhealthy:`, error);
    if (source === SOURCE_GOOGLE && this.googleSession) {
      this.googleSession.invalidate();
    }
    this._reconcile(`error:${source}`);
  }

  /**
   * Mark a source as healthy after a successful tile fetch.
   */
  reportHealthy(source) {
    if (!this.health[source]) return;
    if (!this.health[source].healthy) {
      this.health[source].healthy = true;
      this.health[source].lastError = null;
      this._reconcile(`recovered:${source}`);
    }
  }
}
